/**
 * Sentry initialisation for the app frontend (project `autocampaign-front-end`).
 *
 * Called from main.tsx BEFORE React renders — anything that throws during the
 * first render is only captured if init already ran.
 *
 * Everything is env-driven and Vite inlines `import.meta.env.VITE_*` at BUILD
 * time, so these must be present in the build environment (not just at
 * runtime) — for the Docker image that means `--build-arg`, matching how
 * VITE_API_URL is already handled.
 */
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

/** The environments we intend to exist in Sentry. Anything else is a typo. */
export type SentryEnvironment = 'production' | 'sandbox' | 'development';

function normalize(raw: string | undefined): SentryEnvironment | undefined {
    if (!raw) return undefined;
    const value = raw.toLowerCase();
    if (value === 'production' || value === 'prod') return 'production';
    // "staging"/"preview" are the same tier as sandbox — collapse them so we
    // end up with two real environments rather than four thin ones.
    if (['sandbox', 'staging', 'preview', 'stage'].includes(value)) return 'sandbox';
    if (['development', 'dev', 'local'].includes(value)) return 'development';
    return undefined;
}

/**
 * Sentry environments are not provisioned anywhere — they exist because events
 * arrive carrying this tag. This is the whole of the sandbox/production split.
 */
export const SENTRY_ENVIRONMENT: SentryEnvironment =
    normalize(import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.DEV ? 'development' : 'production');

/** Without a release, every issue reads "unknown release" and regression
 *  detection / suspect commits stay dark. */
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * The API origin this SPA talks to. Sentry only attaches the `sentry-trace` /
 * `baggage` headers to requests matching `tracePropagationTargets` — without
 * the API origin listed, the browser trace and the NestJS trace stay two
 * unrelated traces instead of one end-to-end view. Cross-origin headers also
 * require the API's CORS config to allow them (see the note in docs).
 */
const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000') as string;

/**
 * Runs as a MODULE SIDE EFFECT, not from a call in main.tsx, and that is
 * deliberate: `import` declarations are hoisted, so a call placed above the
 * other imports in main.tsx would still execute after those modules had run
 * their own top-level code. Import *evaluation* order, however, is preserved —
 * so main.tsx importing this file first genuinely initialises Sentry first.
 */
function initSentry(): void {
    // No DSN = no-op. Keeps local dev and any un-provisioned environment quiet
    // instead of erroring, and lets a build opt out by setting VITE_SENTRY_DSN=''.
    if (!DSN) return;

    Sentry.init({
        dsn: DSN,
        environment: SENTRY_ENVIRONMENT,
        release: SENTRY_RELEASE,

        integrations: [
            // React Router v7 data-router instrumentation: turns navigations
            // into spans named by ROUTE PATTERN (/contacts/:id) rather than by
            // URL, so 10k contact pages group into one transaction instead of
            // 10k singletons.
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],

        // Full sampling outside production; 10% in production. This is an
        // authenticated app with steady logged-in traffic, so 100% would be a
        // large and mostly redundant trace volume.
        tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,

        tracePropagationTargets: [/^\//, API_URL],

        enableLogs: true,

        // Noise that is not our bug — browser extensions, cancelled requests,
        // and the chunk-load errors a deploy causes for users mid-session.
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            /^Script error\.?$/,
            /Loading chunk \d+ failed/,
            /Failed to fetch dynamically imported module/,
            'AbortError',
        ],
        denyUrls: [/extensions\//i, /^chrome(-extension)?:\/\//i, /^moz-extension:\/\//i],
    });
}

initSentry();
